/**
 * What one registry row holds, and a store list as the platform reads it — the two value readings
 * the shared parameter controls need (`docs/adr/9024`). Promoted from
 * `src/features/foundations/parameters.ts` and `modules.ts`, which re-export them.
 */

import type { ParameterRow } from '@/api/generated';

export type ParameterValue =
  | { readonly kind: 'numeric'; readonly value: number }
  | { readonly kind: 'text'; readonly value: string }
  /** Neither column is set. Not zero, not blank — nobody has authored this row. */
  | { readonly kind: 'absent' }
  /** Both columns are set, which the contract says never happens. Reported, never picked. */
  | { readonly kind: 'ambiguous' };

/**
 * What one row says, without collapsing the two value columns into one.
 *
 * `ParameterRow`'s own docstring: "both are published rather than collapsed into a string,
 * because a threshold a job compares against and a model version it records are not the
 * same kind of value, and a client that had to guess would guess wrong on the first
 * version identifier that parses as a number." So this does not guess either — and a row
 * carrying both is `ambiguous` rather than silently resolved, because picking one would
 * make the screen disagree with the engine on exactly the row somebody has broken.
 */
export function parameterValue(row: Pick<ParameterRow, 'numericValue' | 'textValue'>): ParameterValue {
  const numeric = row.numericValue ?? null;
  const text = row.textValue ?? null;
  if (numeric !== null && text !== null) return { kind: 'ambiguous' };
  if (numeric !== null) return { kind: 'numeric', value: numeric };
  if (text !== null) return { kind: 'text', value: text };
  return { kind: 'absent' };
}

/** The door keys a text value names: trimmed, de-duplicated, sorted — as the platform reads them. */
export function doorKeys(text: string): readonly string[] {
  return [...new Set(text.split(',').map((door) => door.trim()).filter((door) => door !== ''))].sort();
}
